'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'ar' | 'en';

interface LanguageContextType {
  lang: Language;
  setLanguage: (next: Language) => void;
  toggleLanguage: () => void;
  t: (key: string) => string;
  isRTL: boolean;
  availableLanguages: Language[];
}

const translations: Record<Language, Record<string, string>> = {
  ar: {
    app_name: "سيارتي 360",
    app_subtitle: "Sudan Marketplace",
    search_placeholder: "ابحث عن سيارات، قطع غيار، أو ورش...",
    main_services: "الخدمات الأساسية",
    ai_services: "خدمات الذكاء الاصطناعي",
    account: "الحساب",
    buy_car: "شراء سيارة",
    sell_car: "بيع سيارة",
    parts: "قطع الغيار",
    maintenance: "صيانة ورش",
    diagnose: "تشخيص الأعطال",
    inspection: "الفحص البصري",
    profile: "ملفي الشخصي",
    profile_sub: "My Profile & Settings",
    notifications: "التنبيهات والمقترحات",
    view_all: "مشاهدة الكل",
    admin_link: "الإدارة المركزية",
    lang_btn: "English",
    nav_home: "الرئيسية",
    car_market: "سوق السيارات",
    public_market: "السوق العام",
    maintenance_short: "الصيانة",
    integrated_services: "الخدمات المتكاملة",
    smart_inspection: "الفحص الذكي",
  },
  en: {
    app_name: "MyCar 360",
    app_subtitle: "Sudan Marketplace",
    search_placeholder: "Search for cars, parts, or workshops...",
    main_services: "Main Services",
    ai_services: "AI Services",
    account: "Account",
    buy_car: "Buy Car",
    sell_car: "Sell Car",
    parts: "Spare Parts",
    maintenance: "Workshops",
    diagnose: "AI Diagnose",
    inspection: "AI Vision",
    profile: "My Profile",
    profile_sub: "My Profile & Settings",
    notifications: "Notifications & Suggestions",
    view_all: "View All",
    admin_link: "Central Admin",
    lang_btn: "عربي",
    nav_home: "Home",
    car_market: "Car Market",
    public_market: "Public Market",
    maintenance_short: "Maintenance",
    integrated_services: "Integrated Services",
    smart_inspection: "Smart Inspection",
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Language>('ar');
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>(['ar', 'en']);

  useEffect(() => {
    const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
    const savedLang = localStorage.getItem('app_lang') as Language | null;

    const applyInitialLanguage = (langs: Language[], defaultLang: Language) => {
      const preferred = savedLang && langs.includes(savedLang) ? savedLang : defaultLang;
      setAvailableLanguages(langs);
      setLang(preferred);
      localStorage.setItem('app_lang', preferred);
    };

    fetch(`${API}/api/settings/public/`, { headers: { Accept: 'application/json' } })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        const raw = String(data?.enabled_languages || 'ar,en');
        const parsed = raw
          .split(',')
          .map((v: string) => v.trim().toLowerCase())
          .filter((v: string): v is Language => v === 'ar' || v === 'en');
        const langs: Language[] = parsed.length > 0 ? parsed : ['ar', 'en'];
        const defaultLangRaw = String(data?.default_language || 'ar').toLowerCase();
        const defaultLang: Language = (defaultLangRaw === 'en' && langs.includes('en')) ? 'en' : 'ar';
        applyInitialLanguage(langs, defaultLang);
      })
      .catch(() => {
        const fallbackLangs: Language[] = ['ar', 'en'];
        const fallbackDefault: Language = 'ar';
        applyInitialLanguage(fallbackLangs, fallbackDefault);
      });
  }, []);

  const toggleLanguage = () => {
    if (availableLanguages.length <= 1) return;
    const alternate = availableLanguages.find((l) => l !== lang) || availableLanguages[0];
    setLang(alternate);
    localStorage.setItem('app_lang', alternate);
  };

  const setLanguage = (next: Language) => {
    if (!availableLanguages.includes(next)) return;
    setLang(next);
    localStorage.setItem('app_lang', next);
  };

  const t = (key: string) => {
    return translations[lang][key] || key;
  };

  const isRTL = lang === 'ar';

  return (
    <LanguageContext.Provider value={{ lang, setLanguage, toggleLanguage, t, isRTL, availableLanguages }}>
      <div dir={isRTL ? 'rtl' : 'ltr'} className={lang}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
